/**
 * Defines top-level app routing and guarded pages.
 */
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import Header from "./components/header";
import Footer from "./components/footer";
import Home from "./pages/home";
import Portfolio from "./pages/portfolio";
import Profile from "./pages/profile";
import EditProfile from "./pages/editProfile";
import Membership from "./pages/membership";
import Subscription from "./pages/subscription";
import Contact from "./pages/contact";
import { getSession } from "./scripts/api";
import { LoadingOverlayProvider, useLoadingOverlay } from "./context/LoadingOverlayContext";

/**
 * Gates protected routes behind authenticated session state.
 */
function ProtectedRoute({ children, loading, session }) {
  if (loading) {
    return null;
  }

  return session?.authenticated ? children : <Navigate to="/" replace />;
}

/**
 * Reloads the app when browser restores a cached page.
 */
function RefreshOnPageRestore() {
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  return null;
}

function AppRoutes() {
  const { showLoading, hideLoading } = useLoadingOverlay();
  const [session, setSession] = useState({ authenticated: false, user: null });
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    showLoading("Loading session…");
    try {
      const data = await getSession();
      setSession(data);
    } catch (error) {
      console.error("Failed to load session:", error);
      setSession({ authenticated: false, user: null });
    } finally {
      hideLoading();
      setLoading(false);
    }
  }, [showLoading, hideLoading]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  return (
    <BrowserRouter>
      <RefreshOnPageRestore />

      <div className="app-shell">
        <Header session={session} loading={loading} />

        <main className="flex flex-1 flex-col">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route
              path="/contact"
              element={<Contact session={session} loading={loading} />}
            />
            <Route
              path="/profile/edit"
              element={
                <ProtectedRoute loading={loading} session={session}>
                  <EditProfile
                    session={session}
                    loading={loading}
                    refreshSession={refreshSession}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute loading={loading} session={session}>
                  <Profile
                    session={session}
                    loading={loading}
                    refreshSession={refreshSession}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/membership"
              element={
                <ProtectedRoute loading={loading} session={session}>
                  <Membership session={session} loading={loading} refreshSession={refreshSession} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute loading={loading} session={session}>
                  <Subscription
                    session={session}
                    loading={loading}
                    refreshSession={refreshSession}
                  />
                </ProtectedRoute>
              }
            />
            <Route path="/subscriptions" element={<Navigate to="/membership" replace />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <LoadingOverlayProvider>
      <AppRoutes />
    </LoadingOverlayProvider>
  );
}
